import React from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody} from '../../_metronic/helpers'
import {PermissionLevel} from '../modules/auth/core/_models'

interface LocationState {
  module?: string
  requiredLevel?: 'EDIT'
  currentLevel?: PermissionLevel
  attemptedPath?: string
}

const AccessDenied: React.FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LocationState

  const module = state?.module || 'unknown'
  const requiredLevel = state?.requiredLevel || 'EDIT'
  const currentLevel = state?.currentLevel || 'NONE'
  const attemptedPath = state?.attemptedPath || '/'

  const handleGoBack = () => {
    // Try to go to dashboard first, then profile as fallback
    navigate('/dashboard', {replace: true})
  }

  const getPermissionDisplayName = (level: string) => {
    switch (level) {
      case 'NONE':
        return intl.formatMessage({id: 'USERS.NO_ACCESS'})
      case 'EDIT':
        return intl.formatMessage({id: 'USERS.FULL_ACCESS'})
      default:
        return level
    }
  }

  const getModuleDisplayName = (moduleName: string) => {
    switch (moduleName) {
      case 'dashboard':
        return intl.formatMessage({id: 'MENU.DASHBOARD'})
      case 'products':
        return 'Products'
      case 'orders':
        return 'Orders'
      case 'pos':
        return 'Purchase Orders'
      case 'sales_reports':
        return 'Sales Reports'
      case 'payment_reports':
        return 'Payment Reports'
      case 'stock_reports':
        return 'Stock Reports'
      case 'discounts':
        return 'Discounts'
      case 'daily_deals':
        return 'Daily Deals'
      case 'requests':
        return 'Requests'
      case 'loss_of_opportunity':
        return 'Loss of Opportunity'
      case 'profile':
        return 'Profile'
      default:
        return moduleName.charAt(0).toUpperCase() + moduleName.slice(1)
    }
  }

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <KTCard>
              <KTCardBody className="py-10">
                <div className="text-center">
                  {/* Icon */}
                  <div className="mb-10">
                    <i className="ki-duotone ki-shield-cross fs-3x text-danger">
                      <span className="path1"></span>
                      <span className="path2"></span>
                      <span className="path3"></span>
                    </i>
                  </div>

                  {/* Title */}
                  <h1 className="fw-bold text-gray-900 mb-5">
                    {intl.formatMessage({id: 'ACCESS_DENIED.TITLE'})}
                  </h1>

                  {/* Message */}
                  <div className="fs-5 mb-8">
                    <p className="text-gray-600 mb-4">
                      {intl.formatMessage({id: 'ACCESS_DENIED.MESSAGE'})}
                    </p>
                    
                    <div className="row justify-content-center">
                      <div className="col-md-8">
                        <div className="card bg-light-primary">
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <div className="fw-bold text-gray-800">
                                  {intl.formatMessage({id: 'ACCESS_DENIED.MODULE'})}
                                </div>
                                <div className="text-gray-600">
                                  {getModuleDisplayName(module)}
                                </div>
                              </div>
                              <div className="col-md-6 mb-3">
                                <div className="fw-bold text-gray-800">
                                  {intl.formatMessage({id: 'ACCESS_DENIED.REQUIRED_PERMISSION'})}
                                </div>
                                <div className="text-gray-600">
                                  {getPermissionDisplayName(requiredLevel)}
                                </div>
                              </div>
                              <div className="col-md-6 mb-3">
                                <div className="fw-bold text-gray-800">
                                  {intl.formatMessage({id: 'ACCESS_DENIED.YOUR_PERMISSION'})}
                                </div>
                                <div className="text-gray-600">
                                  {getPermissionDisplayName(currentLevel)}
                                </div>
                              </div>
                              <div className="col-md-6 mb-3">
                                <div className="fw-bold text-gray-800">
                                  Attempted Path
                                </div>
                                <div className="text-gray-600 font-monospace">
                                  {attemptedPath}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="d-flex justify-content-center gap-3">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleGoBack}
                    >
                      {intl.formatMessage({id: 'ACCESS_DENIED.GO_BACK'})}
                    </button>
                    
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => navigate('/myprofile')}
                    >
                      {intl.formatMessage({id: 'MENU.MY_PROFILE'})}
                    </button>
                  </div>
                </div>
              </KTCardBody>
            </KTCard>
          </div>
        </div>
      </div>
    </div>
  )
}

export {AccessDenied}
